import {
  type ClientImageMeta,
  parseClientImageMeta,
} from '@/ai/image/client-meta';

export type WorkspacePrefillScalarValue = string | number | boolean;

export type WorkspacePrefillValue =
  | WorkspacePrefillScalarValue
  | WorkspacePrefillScalarValue[];

export type WorkspacePrefillRequestV1 = {
  version: 1;
  valuesByFieldId: Record<string, WorkspacePrefillValue>;
  filesByFieldId: Record<string, File[]>;
};

export type WorkspaceRemoteImageInput = {
  inputUrl: string;
  previewUrl: string;
  displayName: string;
  clientImageMeta?: ClientImageMeta;
};

type CmsWorkspaceLaunchBaseDetail = {
  launchId: string;
  autoSubmit: boolean;
  defaultToolId: string;
  forceAuthSessionRead?: boolean;
};

export type CmsWorkspaceLaunchDetail = CmsWorkspaceLaunchBaseDetail & {
  inputImage?: WorkspaceRemoteImageInput;
  prefill?: WorkspacePrefillRequestV1;
  resumeJob?: {
    userId: string;
    jobId: string;
    input: Record<string, unknown>;
    originalImageName: string;
  };
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function requireNonEmptyString(value: unknown, path: string) {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`${path} must be a non-empty string.`);
  }

  return value.trim();
}

function isPrefillScalar(value: unknown): value is WorkspacePrefillScalarValue {
  return (
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  );
}

function normalizePrefillValue(value: unknown, path: string) {
  if (isPrefillScalar(value)) {
    return value;
  }

  if (Array.isArray(value) && value.every(isPrefillScalar)) {
    return [...value];
  }

  throw new Error(`${path} must be a scalar value or scalar array.`);
}

function isBrowserFile(value: unknown): value is File {
  return typeof File !== 'undefined' && value instanceof File;
}

function normalizeWorkspaceRemoteImageInput(
  value: unknown
): WorkspaceRemoteImageInput {
  if (!isRecord(value)) {
    throw new Error('Workspace launch inputImage must be an object.');
  }

  const clientImageMeta =
    value.clientImageMeta === undefined
      ? null
      : parseClientImageMeta(value.clientImageMeta);
  if (clientImageMeta && !clientImageMeta.ok) {
    throw new Error('Workspace launch inputImage clientImageMeta is invalid.');
  }

  return {
    inputUrl: requireNonEmptyString(
      value.inputUrl,
      'Workspace launch inputImage inputUrl'
    ),
    previewUrl: requireNonEmptyString(
      value.previewUrl,
      'Workspace launch inputImage previewUrl'
    ),
    displayName: requireNonEmptyString(
      value.displayName,
      'Workspace launch inputImage displayName'
    ),
    ...(clientImageMeta?.ok ? { clientImageMeta: clientImageMeta.data } : {}),
  };
}

export function normalizeWorkspacePrefillRequest(
  value: unknown
): WorkspacePrefillRequestV1 {
  if (!isRecord(value)) {
    throw new Error('Workspace prefill must be an object.');
  }
  if (value.version !== 1) {
    throw new Error('Workspace prefill version must be 1.');
  }
  if (!isRecord(value.valuesByFieldId)) {
    throw new Error('Workspace prefill valuesByFieldId must be an object.');
  }
  if (!isRecord(value.filesByFieldId)) {
    throw new Error('Workspace prefill filesByFieldId must be an object.');
  }

  const valuesByFieldId = Object.fromEntries(
    Object.entries(value.valuesByFieldId).map(([fieldId, fieldValue]) => {
      const normalizedFieldId = requireNonEmptyString(
        fieldId,
        'Workspace prefill value field ID'
      );
      return [
        normalizedFieldId,
        normalizePrefillValue(
          fieldValue,
          `Workspace prefill field ${normalizedFieldId}`
        ),
      ];
    })
  );
  const filesByFieldId = Object.fromEntries(
    Object.entries(value.filesByFieldId).map(([fieldId, files]) => {
      const normalizedFieldId = requireNonEmptyString(
        fieldId,
        'Workspace prefill file field ID'
      );
      if (!Array.isArray(files) || !files.every(isBrowserFile)) {
        throw new Error(
          `Workspace prefill file field ${normalizedFieldId} must contain only File values.`
        );
      }
      return [normalizedFieldId, [...files]];
    })
  );

  return {
    version: 1,
    valuesByFieldId,
    filesByFieldId,
  };
}

export function normalizeCmsWorkspaceLaunchDetail(
  value: unknown
): CmsWorkspaceLaunchDetail {
  if (!isRecord(value)) {
    throw new Error('Workspace launch detail must be an object.');
  }

  const launchId = requireNonEmptyString(
    value.launchId,
    'Workspace launch launchId'
  );
  const defaultToolId = requireNonEmptyString(
    value.defaultToolId,
    'Workspace launch defaultToolId'
  );
  if (
    value.forceAuthSessionRead !== undefined &&
    typeof value.forceAuthSessionRead !== 'boolean'
  ) {
    throw new Error('Workspace launch forceAuthSessionRead must be a boolean.');
  }

  const baseDetail = {
    launchId,
    autoSubmit: value.autoSubmit === true,
    defaultToolId,
    forceAuthSessionRead: value.forceAuthSessionRead,
  };
  if ('files' in value || 'inputIntent' in value) {
    throw new Error(
      'Workspace launch legacy files/inputIntent fields are not supported.'
    );
  }

  let resumeJob: CmsWorkspaceLaunchDetail['resumeJob'];
  if (value.resumeJob !== undefined) {
    const job = value.resumeJob;
    if (
      !isRecord(job) ||
      !isRecord(job.input) ||
      value.autoSubmit === true ||
      value.prefill !== undefined ||
      value.inputImage !== undefined
    ) {
      throw new Error(
        'Workspace job resume must attach an existing job without submitting.'
      );
    }
    resumeJob = {
      userId: requireNonEmptyString(job.userId, 'Workspace resume userId'),
      jobId: requireNonEmptyString(job.jobId, 'Workspace resume jobId'),
      originalImageName: requireNonEmptyString(
        job.originalImageName,
        'Workspace resume originalImageName'
      ),
      input: job.input,
    };
  }

  return {
    ...baseDetail,
    ...(resumeJob ? { resumeJob } : {}),
    inputImage:
      value.inputImage === undefined
        ? undefined
        : normalizeWorkspaceRemoteImageInput(value.inputImage),
    prefill:
      value.prefill === undefined
        ? undefined
        : normalizeWorkspacePrefillRequest(value.prefill),
  };
}

export function createCmsWorkspaceLaunchId() {
  const randomUUID = globalThis.crypto?.randomUUID;
  if (!randomUUID) {
    throw new Error('Workspace launch requires crypto.randomUUID().');
  }
  return randomUUID.call(globalThis.crypto);
}
